<template>
  <div class="pricing-edit w-100">
    <h3 class="section-title">가격 설정</h3>

    <!-- 로딩 중일 때 표시 -->
    <div v-if="processing" class="loading-container">
      <b-spinner variant="primary"></b-spinner>
      <p>{{ progressMessage || '가격 설정 작업 중...' }}</p>
    </div>

    <!-- 가격 정책 설정 및 결과 동시 표시 -->
    <div v-else class="pricing-content">
      <div class="row mb-4">
        <!-- 왼쪽: 가격 정책 설정 패널 -->
        <div class="col-md-2">
          <div class="card h-100">
            <div class="card-header bg-light">
              <span class="font-weight-bold">가격 정책 설정</span>
            </div>
            <div class="card-body pricing-policy-panel">
              <div class="form-group">
                <label>적용 환율 (CNY → KRW)</label>
                <input type="number" class="form-control" v-model.number="editExchangeRate" min="150" max="400"
                  @change="updatePricing" />
              </div>

              <div class="form-group">
                <label>공급 마진율 (%)</label>
                <input type="number" class="form-control" v-model.number="editMarginRate" min="0" max="90"
                  @change="updatePricing" />
              </div>

              <div class="form-group">
                <label>쿠팡 마진율 (%)</label>
                <input type="number" class="form-control" v-model.number="editCoupangMarginRate" min="0" max="90"
                  @change="updatePricing" />
              </div>

              <div class="form-group">
                <label>반올림 단위 (원)</label>
                <select v-model.number="editRoundUnit" class="form-control" @change="updatePricing">
                  <option :value="1">1원 단위</option>
                  <option :value="10">10원 단위</option>
                  <option :value="100">100원 단위</option>
                  <option :value="1000">1,000원 단위</option>
                </select>
              </div>

              <div class="form-group">
                <div class="custom-control custom-switch">
                  <input type="checkbox" class="custom-control-input" id="minSupplyMarginSwitch" v-model="editEnableMinSupplyMargin"
                    @change="updatePricing" />
                  <label class="custom-control-label" for="minSupplyMarginSwitch">최소 공급 마진 보장</label>
                </div>
                <small class="form-text text-muted">쿠팡 마진율은 고정하고 원가율을 조정하여 최소 공급 마진액을 보장합니다.</small>
              </div>

              <div v-if="editEnableMinSupplyMargin" class="form-group">
                <label>최소 공급 마진액 (원)</label>
                <input type="number" class="form-control" v-model.number="editMinSupplyMarginAmount" min="0" step="100"
                  @change="updatePricing" />
                <small class="form-text text-muted">공급가 = 원가 + 이 금액, 판매가 = 공급가 ÷ (1 - 쿠팡마진율)로 계산됩니다.</small>
              </div>

              <div class="form-group">
                <div class="custom-control custom-switch">
                  <input type="checkbox" class="custom-control-input" id="bundlingSwitch" v-model="editEnableBundling"
                    @change="updatePricing" />
                  <label class="custom-control-label" for="bundlingSwitch">번들링 활성화</label>
                </div>
                <small class="form-text text-muted">낮은 마진 상품을 자동으로 묶어서 판매합니다.</small>
              </div>

              <div v-if="editEnableBundling" class="bundling-details mt-3">
                <div class="form-group">
                  <label>번들링 기준</label>
                  <div class="custom-control custom-radio">
                    <input type="radio" id="marginAmountRadio" name="bundlingType" class="custom-control-input"
                      value="marginAmount" v-model="editBundlingType" @change="updatePricing" />
                    <label class="custom-control-label" for="marginAmountRadio">최소 공급 마진액 기준</label>
                  </div>
                  <div class="custom-control custom-radio">
                    <input type="radio" id="coupangMarginAmountRadio" name="bundlingType" class="custom-control-input"
                      value="coupangMarginAmount" v-model="editBundlingType" @change="updatePricing" />
                    <label class="custom-control-label" for="coupangMarginAmountRadio">최소 쿠팡 마진액 기준</label>
                  </div>
                </div>

                <div v-if="editBundlingType === 'marginAmount'" class="form-group">
                  <label>최소 공급 마진액 (원)</label>
                  <input type="number" class="form-control" v-model.number="editMinMarginAmount" min="1000" step="1000"
                    @change="updatePricing" />
                </div>

                <div v-else class="form-group">
                  <label>최소 쿠팡 마진액 (원)</label>
                  <input type="number" class="form-control" v-model.number="editMinCoupangMarginAmount" min="1000"
                    step="1000" @change="updatePricing" />
                </div>
              </div>

              <div class="mt-3">
                <b-button variant="outline-primary" @click="updatePricing" class="btn-block mb-2">
                  <i class="fas fa-calculator mr-1"></i> 일괄 재계산
                </b-button>
                <b-button variant="primary" @click="savePricing" class="btn-block">저장하기</b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 오른쪽: 옵션별 가격 정보 테이블 (CDATATABLE 사용) -->
        <div class="col-md-10">
          <div class="card h-100">
            <div class="card-header bg-light d-flex justify-content-between align-items-center">
              <span class="font-weight-bold">옵션별 가격 정보</span>
              <div class="d-flex gap-2">
                <b-button size="sm" variant="danger" @click="deleteSelectedOptions"
                  :disabled="selectedOptions.length === 0">
                  선택 삭제 ({{ selectedOptions.length }})
                </b-button>
                <b-button size="sm" variant="success" @click="addBundleOptions"
                  :disabled="selectedOptions.length === 0">
                  번들 추가
                </b-button>
                <b-button size="sm" variant="warning" @click="applyBundleToOptions"
                  :disabled="selectedOptions.length === 0">
                  번들 적용
                </b-button>
              </div>
            </div>
            <div class="card-body p-0">
              <CDATATABLE
                :headers="tableHeaders"
                :items="calculatedOptions"
                itemKey="optionNumber"
                :show-select="true"
                :fixedColumns="'옵션명'"
                :height="'600px'"
                :items-per-page="20"
                v-model="selectedOptions"
              >
                <!-- 옵션명 슬롯 -->
                <template v-slot:옵션명="{ item }">
                  <div>
                    <div class="font-weight-medium">
                      {{ getOptionName(item) }}
                      <b-badge v-if="item.isBundleOption" variant="info" class="ml-1">번들</b-badge>
                    </div>
                    <small class="text-muted">{{ item.optionNumber }}</small>
                  </div>
                </template>

                <!-- 옵션번호 슬롯 -->
                <template v-slot:옵션번호="{ item }">
                  <small class="text-muted">{{ item.optionNumber }}</small>
                </template>

                <!-- 번들수량 슬롯 -->
                <template v-slot:번들수량="{ item }">
                  <div class="text-center">
                    <span :class="{ 'font-weight-bold text-primary': item.bundleQuantity > 1 }">
                      {{ item.bundleQuantity }}개
                    </span>
                  </div>
                </template>

                <!-- 재고 슬롯 -->
                <template v-slot:재고="{ item }">
                  <div class="text-center">
                    <span :class="{ 'font-weight-bold text-primary': item.bundleQuantity > 1 }">
                      {{ (item.stock || 0) * (item.bundleQuantity || 1) }}
                    </span>
                    <span v-if="item.bundleQuantity > 1" class="text-muted small d-block">
                      ({{ item.stock || 0 }} × {{ item.bundleQuantity }})
                    </span>
                  </div>
                </template>

                <!-- 중국원가 슬롯 -->
                <template v-slot:중국원가="{ item, index }">
                  <div class="text-right">
                    <div class="input-group input-group-sm mb-1">
                      <div class="input-group-prepend">
                        <span class="input-group-text">¥</span>
                      </div>
                      <input
                        type="number"
                        class="form-control text-right"
                        v-model.number="item.price"
                        min="0"
                        step="0.1"
                        @change="recalculateOption(index)"
                      />
                    </div>
                    <div class="text-muted small">
                      {{ formatNumber(item.price * (item.bundleQuantity || 1) * editExchangeRate) }}원
                    </div>
                    <div v-if="item.bundleQuantity > 1" class="text-muted small">
                      (¥{{ formatNumber(item.price * (item.bundleQuantity || 1)) }})
                    </div>
                  </div>
                </template>

                <!-- 공급가 슬롯 -->
                <template v-slot:공급가="{ item }">
                  <div class="input-group input-group-sm">
                    <input
                      type="number"
                      class="form-control text-right"
                      v-model.number="item.supplyPrice"
                      min="0"
                      step="100"
                      @input="recalculateMarginsByOptionNumber(item.optionNumber)"
                    />
                    <div class="input-group-append">
                      <span class="input-group-text">원</span>
                    </div>
                  </div>
                </template>

                <!-- 공급마진 슬롯 -->
                <template v-slot:공급마진="{ item }">
                  <div class="text-right">
                    <div :class="{ 'text-success': item.supplyMargin > 0, 'text-danger': item.supplyMargin < 0 }">
                      {{ formatNumber(item.supplyMargin) }}원
                    </div>
                    <div class="text-muted small">
                      ({{ calculateMarginRate(item.purchasePrice, item.supplyPrice) }}%)
                    </div>
                  </div>
                </template>

                <!-- 판매가 슬롯 -->
                <template v-slot:판매가="{ item }">
                  <div class="input-group input-group-sm">
                    <input
                      type="number"
                      class="form-control text-right"
                      v-model.number="item.sellingPrice"
                      min="0"
                      step="100"
                      @input="recalculateMarginsByOptionNumber(item.optionNumber)"
                    />
                    <div class="input-group-append">
                      <span class="input-group-text">원</span>
                    </div>
                  </div>
                </template>

                <!-- 쿠팡마진 슬롯 -->
                <template v-slot:쿠팡마진="{ item }">
                  <div class="text-right">
                    <div :class="{ 'text-success': item.sellingMargin > 0, 'text-danger': item.sellingMargin < 0 }">
                      {{ formatNumber(item.sellingMargin) }}원
                    </div>
                    <div class="text-muted small">
                      ({{ calculateMarginRate(item.supplyPrice, item.sellingPrice) }}%)
                    </div>
                  </div>
                </template>
              </CDATATABLE>
            </div>
          </div>
        </div>
      </div>

      <!-- 가격 구성 비율 시각화 -->
      <div class="card mb-4">
        <div class="card-header bg-light">
          <span>가격 구성 비율</span>
        </div>
        <div class="card-body">
          <div class="row mb-2">
            <div class="col-md-4 text-center">상품 원가</div>
            <div class="col-md-4 text-center">공급 마진</div>
            <div class="col-md-4 text-center">쿠팡 마진</div>
          </div>
          <div class="progress mb-3" style="height: 30px; border-radius: 5px;">
            <div class="progress-bar bg-warning" role="progressbar" :style="{ width: costRatio + '%' }"
              :aria-valuenow="costRatio" aria-valuemin="0" aria-valuemax="100">
              {{ costRatio.toFixed(0) }}%
            </div>
            <div class="progress-bar bg-success" role="progressbar" :style="{ width: editMarginRate + '%' }"
              :aria-valuenow="editMarginRate" aria-valuemin="0" aria-valuemax="100">
              {{ editMarginRate.toFixed(0) }}%
            </div>
            <div class="progress-bar bg-primary" role="progressbar" :style="{ width: editCoupangMarginRate + '%' }"
              :aria-valuenow="editCoupangMarginRate" aria-valuemin="0" aria-valuemax="100">
              {{ editCoupangMarginRate.toFixed(0) }}%
            </div>
          </div>
        </div>
      </div>

      <!-- 전체 가격 요약 카드 -->
      <div class="card mb-4">
        <div class="card-header bg-light">
          <span>가격 요약</span>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-4">
              <div class="card border-light mb-3">
                <div class="card-header bg-transparent border-light">평균 공급가</div>
                <div class="card-body">
                  <h5 class="card-title">{{ formatNumber(averageSupplyPrice) }}원</h5>
                  <p class="card-text">평균 공급 마진: {{ formatNumber(averageSupplyMargin) }}원</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-light mb-3">
                <div class="card-header bg-transparent border-light">평균 판매가</div>
                <div class="card-body">
                  <h5 class="card-title">{{ formatNumber(averageSellingPrice) }}원</h5>
                  <p class="card-text">평균 쿠팡 마진: {{ formatNumber(averageSellingMargin) }}원</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-light mb-3">
                <div class="card-header bg-transparent border-light">평균 마진율</div>
                <div class="card-body">
                  <h5 class="card-title">공급: {{ averageSupplyMarginRate }}%</h5>
                  <p class="card-text">쿠팡: {{ averageCoupangMarginRate }}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 다음 단계 버튼 -->
      <div class="navigation-buttons mt-4 text-center">
        <b-button variant="primary" @click="savePricing" class="mr-2">
          저장하기
        </b-button>
        <!-- <b-button variant="success" @click="goToNextStep">
          다음 단계로 (SEO 설정)
        </b-button> -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import CDATATABLE from '@/components/common/CDataTable.vue';
import bundleOptionService from '@/services/works/bundleOptionService';
import pricingPolicy from '@/services/works/pricingPolicy';

export default {
  name: "PricingEdit",
  components: {
    CDATATABLE
  },
  data() {
    return {
      // 가격 정책 편집 데이터
      editExchangeRate: 350,
      editMarginRate: 40,
      editCoupangMarginRate: 40,
      editRoundUnit: 100,
      editEnableMinSupplyMargin: false,
      editMinSupplyMarginAmount: 3000,
      editEnableBundling: false,
      editBundlingType: 'marginAmount',
      editMinMarginAmount: 3000,
      editMinCoupangMarginAmount: 3000,

      // 옵션 가격 계산 결과 캐시
      calculatedOptions: [],

      // CDATATABLE 관련
      selectedOptions: [],

      // 테이블 헤더 정의
      tableHeaders: [
        { text: '옵션명', value: '옵션명', width: '200px', sortable: true, fixed: true, isSlot: true },
        { text: '옵션번호', value: '옵션번호', width: '60px', sortable: false, isSlot: true },
        { text: '번들수량', value: '번들수량', width: '60px', sortable: true, align: 'center', isSlot: true },
        { text: '재고', value: '재고', width: '60px', sortable: true, align: 'center', isSlot: true },
        { text: '중국원가(CNY)', value: '중국원가', width: '60px', sortable: true, align: 'right', isSlot: true },
        { text: '공급가(KRW)', value: '공급가', width: '130px', sortable: true, align: 'right', isSlot: true },
        { text: '공급마진', value: '공급마진', width: '80px', sortable: true, align: 'right', isSlot: true },
        { text: '판매가(KRW)', value: '판매가', width: '130px', sortable: true, align: 'right', isSlot: true },
        { text: '쿠팡마진', value: '쿠팡마진', width: '80px', sortable: true, align: 'right', isSlot: true }
      ]
    };
  },
  computed: {
    ...mapState({
      selectedProduct: state => state.selectedRocketProduct,
      processing: state => state.processing,
      progressMessage: state => state.progressMessage,
      settings: state => state.settings[0]?.defaultSettings || {}
    }),

    // 제품 옵션 리스트
    productOptions() {
      return this.selectedProduct?.options || [];
    },

    // 원가 비율
    costRatio() {
      // 100% - 마진율 - 쿠팡마진율
      return Math.max(1, 100 - this.editMarginRate - this.editCoupangMarginRate);
    },

    // 가격 정책 정보
    pricingInfo() {
      return {
        exchangeRate: this.editExchangeRate,
        marginRate: this.editMarginRate,
        coupangMarginRate: this.editCoupangMarginRate,
        roundUnit: this.editRoundUnit,
        enableMinSupplyMargin: this.editEnableMinSupplyMargin,
        minSupplyMarginAmount: this.editMinSupplyMarginAmount,
        enableBundling: this.editEnableBundling,
        bundlingType: this.editBundlingType,
        minMarginAmount: this.editMinMarginAmount,
        minCoupangMarginAmount: this.editMinCoupangMarginAmount
      };
    },

    // 묶음 계산용 설정 — 화면에서 지금 편집 중인 값 그대로. bundleOptionService 에 인자로 넘긴다.
    bundleSettings() {
      return {
        ...this.pricingInfo,
        msrpMultiplier: this.settings.msrpMultiplier || 1.3,
        msrpRoundUnit: this.settings.roundUnit || this.editRoundUnit || 100
      };
    },

    // 평균 공급가
    averageSupplyPrice() {
      if (!this.calculatedOptions.length) return 0;
      const total = this.calculatedOptions.reduce((sum, opt) => sum + opt.supplyPrice, 0);
      return Math.round(total / this.calculatedOptions.length);
    },

    // 평균 판매가
    averageSellingPrice() {
      if (!this.calculatedOptions.length) return 0;
      const total = this.calculatedOptions.reduce((sum, opt) => sum + opt.sellingPrice, 0);
      return Math.round(total / this.calculatedOptions.length);
    },

    // 평균 공급 마진
    averageSupplyMargin() {
      if (!this.calculatedOptions.length) return 0;
      const total = this.calculatedOptions.reduce((sum, opt) => sum + opt.supplyMargin, 0);
      return Math.round(total / this.calculatedOptions.length);
    },

    // 평균 쿠팡 마진
    averageSellingMargin() {
      if (!this.calculatedOptions.length) return 0;
      const total = this.calculatedOptions.reduce((sum, opt) => sum + opt.sellingMargin, 0);
      return Math.round(total / this.calculatedOptions.length);
    },

    // 평균 공급 마진율
    averageSupplyMarginRate() {
      if (!this.calculatedOptions.length) return 0;
      const rates = this.calculatedOptions.map(opt =>
        this.calculateMarginRate(opt.purchasePrice, opt.supplyPrice)
      );
      const total = rates.reduce((sum, rate) => sum + rate, 0);
      return Math.round(total / rates.length);
    },

    // 평균 쿠팡 마진율
    averageCoupangMarginRate() {
      if (!this.calculatedOptions.length) return 0;
      const rates = this.calculatedOptions.map(opt =>
        this.calculateMarginRate(opt.supplyPrice, opt.sellingPrice)
      );
      const total = rates.reduce((sum, rate) => sum + rate, 0);
      return Math.round(total / rates.length);
    }
  },
  watch: {
    // 선택된 상품이 변경되면 기본 설정 로드
    selectedProduct: {
      handler(newProduct) {
        if (newProduct) {
          this.loadDefaultSettings();
          this.initializeCalculatedOptions();
        }
      },
      immediate: true
    },

    // 설정이 변경되면 편집 데이터 업데이트
    settings: {
      handler() {
        this.loadDefaultSettings();
      },
      immediate: true
    }
  },
  methods: {
    // 기본 설정 로드
    //
    // 상품에 저장된 설정(pricing.info)이 화면 기본세팅보다 앞선다. 그 판단은 pricingPolicy
    // 하나가 하고, 여기서는 푼 값을 편집 필드에 옮기기만 한다 — 자동 작업 경로(pricingService)와
    // 같은 함수를 써야 화면에 보이는 가격과 작업개시가 계산하는 가격이 갈리지 않는다.
    loadDefaultSettings() {
      const policy = pricingPolicy.resolve(this.selectedProduct, this.settings);

      this.editExchangeRate = policy.exchangeRate;
      this.editMarginRate = policy.marginRate;
      this.editCoupangMarginRate = policy.coupangMarginRate;
      this.editRoundUnit = policy.roundUnit;
      this.editEnableMinSupplyMargin = policy.enableMinSupplyMargin;
      this.editMinSupplyMarginAmount = policy.minSupplyMarginAmount;
      this.editEnableBundling = policy.enableBundling;
      this.editBundlingType = policy.bundlingType;
      this.editMinMarginAmount = policy.minMarginAmount;
      this.editMinCoupangMarginAmount = policy.minCoupangMarginAmount;
    },

    // 계산된 옵션 초기화
    // calculatedOptions 에 실을 묶음 플래그. savePricing 이 이걸 그대로 product.options 에 저장하므로
    // 셋 중 어느 경로로 만들어져도 빠지면 안 된다.
    bundleFieldsOf(option) {
      const info = bundleOptionService.resolveBundleInfo(option);

      return {
        isBundleOption: !!info,
        originalOptionNumber: info ? info.originalOptionNumber : null
      };
    },

    initializeCalculatedOptions() {
      if (!this.selectedProduct || !this.selectedProduct.options) return;

      console.log('🔧 initializeCalculatedOptions 시작');
      console.log('📦 product.options:', this.selectedProduct.options);

      // 상품에 저장된 가격 정보가 있으면 사용하되, 현재 옵션과 일치하는지 확인
      if (this.selectedProduct.pricing && this.selectedProduct.pricing.options) {
        const currentOptionNumbers = this.selectedProduct.options.map(opt => opt.optionNumber);
        const pricingOptionNumbers = this.selectedProduct.pricing.options.map(opt => opt.optionNumber);

        // 현재 옵션과 pricing.options가 일치하는지 확인
        const isMatched = currentOptionNumbers.length === pricingOptionNumbers.length &&
                         currentOptionNumbers.every(num => pricingOptionNumbers.includes(num));

        if (isMatched) {
          // 일치하면 저장된 가격 정보 사용
          const savedOptions = JSON.parse(JSON.stringify(this.selectedProduct.pricing.options));

          // 묶음 플래그 복원 — pricing.options 에는 이 두 필드가 없다.
          // 없는 채로 두면 savePricing 이 `opt.isBundleOption || false` 로 product.options 에
          // false/null 을 굳혀버려, 화면을 다시 열고 저장하는 것만으로 묶음 표시가 사라진다.
          // 대입 전에 넣어야 한다 (Vue 2 는 대입 후 새로 생긴 속성을 추적하지 않는다).
          savedOptions.forEach(opt => {
            const productOption = this.selectedProduct.options.find(o => o.optionNumber === opt.optionNumber);
            Object.assign(opt, this.bundleFieldsOf(productOption || opt));
          });

          this.calculatedOptions = savedOptions;

          // korName이 없는 경우 product.options에서 가져와서 채우기
          this.calculatedOptions.forEach(opt => {
            if (!opt.korName) {
              // 번들 옵션인지 확인 (optionNumber가 "원본B숫자" 형식)
              const isBundleOption = /B\d+$/.test(opt.optionNumber);
              let originalOption = null;

              if (isBundleOption) {
                // 번들 옵션: 원본 옵션 번호 추출 (예: "251125001001B3" -> "251125001001")
                const originalOptionNumber = opt.optionNumber.replace(/B\d+$/, '');
                originalOption = this.selectedProduct.options.find(
                  o => o.optionNumber === originalOptionNumber
                );

                if (originalOption && originalOption.korName) {
                  // 번들 수량 추출
                  const bundleQuantity = opt.bundleQuantity || parseInt(opt.optionNumber.match(/B(\d+)$/)[1]);
                  opt.korName = `${originalOption.korName} (${bundleQuantity}개묶음)`;
                  console.log(`✅ 번들 korName 복원: ${opt.optionNumber} -> ${opt.korName}`);
                }
              } else {
                // 일반 옵션: 직접 찾기
                originalOption = this.selectedProduct.options.find(
                  o => o.optionNumber === opt.optionNumber
                );

                if (originalOption && originalOption.korName) {
                  opt.korName = originalOption.korName;
                  console.log(`✅ korName 복원: ${opt.optionNumber} -> ${opt.korName}`);
                }
              }
            }
          });

          console.log('💰 저장된 가격 정보 사용 (옵션 일치)');
          console.log('💰 사용된 pricing.options:', this.calculatedOptions);
        } else {
          // 일치하지 않으면 현재 옵션을 기반으로 새로 계산
          console.log('⚠️ 옵션 불일치 감지 - 새로 계산');
          console.log('현재 옵션:', currentOptionNumbers);
          console.log('저장된 가격 옵션:', pricingOptionNumbers);
          this.calculatedOptions = this.selectedProduct.options.map(option => ({
            optionNumber: option.optionNumber,
            optionName: this.getOptionName(option),
            korName: option.korName,
            price: option.price || 0,
            stock: option.stock || 0,
            bundleQuantity: option.bundleQuantity || 1,
            option1: option.option1,
            option2: option.option2,
            ...this.bundleFieldsOf(option)
          }));
          console.log('✅ calculatedOptions 생성 완료:', this.calculatedOptions);
          this.updatePricing();
        }
      }
      // 저장된 가격 정보가 없으면 옵션을 기반으로 계산
      else {
        console.log('📝 저장된 가격 정보 없음 - 새로 계산');
        this.calculatedOptions = this.selectedProduct.options.map(option => ({
          optionNumber: option.optionNumber,
          optionName: this.getOptionName(option),
          korName: option.korName,
          price: option.price || 0,
          stock: option.stock || 0,
          bundleQuantity: option.bundleQuantity || 1,
          option1: option.option1,
          option2: option.option2,
          ...this.bundleFieldsOf(option)
        }));
        console.log('✅ calculatedOptions 생성 완료:', this.calculatedOptions);
        this.updatePricing();
      }
    },

    // 가격 정책 변경 시 모든 옵션의 가격 다시 계산
    updatePricing() {
      if (!this.calculatedOptions.length) return;

      this.calculatedOptions.forEach((_option, index) => {
        this.recalculateOption(index);
      });
    },

    // 소싱 목표가(가장 싼 옵션 기준)를 이 옵션에 맞춘 값 — AIRocketReg.updateOptionPricing 과 같은 규칙
    scaledSourcingPricing(option) {
      const p = this.selectedProduct;
      return pricingPolicy.scaleFixedPricing(
        { sellingPrice: Number(p.sourcingSellingPrice), purchaseCost: p.sourcingPurchaseCost || null, supplyPrice: p.sourcingSupplyPrice || null },
        option.price, pricingPolicy.referenceCny(this.calculatedOptions), option.bundleQuantity || 1,
        this.editPolicy(), p.sourcingBundleQuantity || null);
    },

    // 매입가 계산용 정책 — 적용환율·반올림은 편집 중인 값, 통합통관 모드는 기본세팅 스위치
    editPolicy() {
      return {
        ...pricingPolicy.resolve(this.selectedProduct, this.settings),
        exchangeRate: this.editExchangeRate,
        roundUnit: this.editRoundUnit
      };
    },

    // 개별 옵션 재계산
    recalculateOption(index) {
      const option = this.calculatedOptions[index];
      if (!option) return;

      // 매입가 계산 (CNY × 환율 × 번들수량)
      const purchasePrice = pricingPolicy.purchaseCost(option.price * option.bundleQuantity, this.editPolicy());

      let supplyPrice, sellingPrice, supplyMargin, sellingMargin;

      // 소싱에서 목표 판매가가 정해진 상품(sourcingSellingPrice)은 판매가를 고정하고 공급가만 쿠팡마진율로 푼다.
      // 마진율·최소공급마진·자동 번들은 쓰지 않는다. 역마진이면 가격을 쓰지 않고 알린다.
      const fixedSellingPrice = this.selectedProduct && this.selectedProduct.sourcingSellingPrice;
      if (fixedSellingPrice) {
        try {
          // 소싱 매입원가(sourcingPurchaseCost)가 실려 있으면 매입가는 그 값이다.
          // 소싱 값은 가장 싼 옵션 기준 — 더 비싼 옵션은 판매가·매입원가를 올린다(scaleFixedPricing).
          const scaled = this.scaledSourcingPricing(option);
          const sourcingCost = Number(scaled.purchaseCost) || 0;
          const fixedPurchase = sourcingCost > 0 ? sourcingCost : purchasePrice;
          const fixed = pricingPolicy.fixedSellingPricing(scaled.sellingPrice, fixedPurchase,
            { coupangMarginRate: this.editCoupangMarginRate, roundUnit: this.editRoundUnit }, `옵션 ${option.optionNumber}`,
            scaled.supplyPrice, this.$store?.state?.settings?.[0]?.rocketMarginGate);
          option.purchasePrice = fixedPurchase;
          option.supplyPrice = fixed.supplyPrice;
          option.sellingPrice = fixed.sellingPrice;
          option.supplyMargin = fixed.supplyMargin;
          option.sellingMargin = fixed.sellingMargin;
        } catch (error) {
          console.error('[PricingEdit] 목표 판매가 계산 실패:', error.message);
          this.$bvToast.toast(error.message, { title: '공급마진 부족', variant: 'danger', solid: true });
        }
        return;
      }

      // 최소 공급 마진 보장이 활성화된 경우
      if (this.editEnableMinSupplyMargin) {
        // 새로운 로직: 쿠팡 마진율 고정, 최소 공급 마진액 보장
        supplyPrice = purchasePrice + this.editMinSupplyMarginAmount;
        supplyPrice = Math.round(supplyPrice / this.editRoundUnit) * this.editRoundUnit;

        // 판매가 = 공급가 ÷ (1 - 쿠팡마진율)
        const coupangRateDecimal = this.editCoupangMarginRate / 100;
        const rawSellingPrice = supplyPrice / (1 - coupangRateDecimal);
        sellingPrice = Math.round(rawSellingPrice / this.editRoundUnit) * this.editRoundUnit;

        // 마진 계산
        supplyMargin = supplyPrice - purchasePrice;
        sellingMargin = sellingPrice - supplyPrice;

        console.log(`최소 공급 마진 보장 계산 (옵션 ${option.optionNumber}):`, {
          원가: purchasePrice,
          최소공급마진: this.editMinSupplyMarginAmount,
          공급가: supplyPrice,
          실제공급마진: supplyMargin,
          쿠팡마진율: this.editCoupangMarginRate + '%',
          판매가: sellingPrice,
          쿠팡마진: sellingMargin
        });
      } else {
        // 기존 로직: 마진율 기반 계산
        const marginRateDecimal = this.editMarginRate / 100;
        const rawSupplyPrice = purchasePrice / (1 - marginRateDecimal);
        supplyPrice = Math.round(rawSupplyPrice / this.editRoundUnit) * this.editRoundUnit;

        // 판매가 계산 (공급가 ÷ (1 - 쿠팡마진율/100))
        const coupangRateDecimal = this.editCoupangMarginRate / 100;
        const rawSellingPrice = supplyPrice / (1 - coupangRateDecimal);
        sellingPrice = Math.round(rawSellingPrice / this.editRoundUnit) * this.editRoundUnit;

        // 마진 계산
        supplyMargin = supplyPrice - purchasePrice;
        sellingMargin = sellingPrice - supplyPrice;
      }

      // 번들 수량 자동 계산 (번들링 활성화된 경우)
      // 단, 소싱에서 묶음 수량이 정해진 상품(sourcingBundleQuantity)은 그 값을 지킨다.
      if (this.editEnableBundling && !(this.selectedProduct && this.selectedProduct.sourcingBundleQuantity)) {
        option.bundleQuantity = this.calculateBundleQuantity(
          option.price,
          this.editExchangeRate,
          this.editMarginRate,
          this.editCoupangMarginRate,
          this.editBundlingType,
          this.editMinMarginAmount,
          this.editMinCoupangMarginAmount,
          this.editRoundUnit
        );
      }

      // 옵션 업데이트
      option.purchasePrice = purchasePrice;
      option.supplyPrice = supplyPrice;
      option.sellingPrice = sellingPrice;
      option.supplyMargin = supplyMargin;
      option.sellingMargin = sellingMargin;
    },

    // 마진 재계산 (공급가나 판매가가 직접 수정됐을 때) - optionNumber로 찾기
    recalculateMarginsByOptionNumber(optionNumber) {
      // optionNumber로 원본 calculatedOptions에서 인덱스 찾기
      const index = this.calculatedOptions.findIndex(opt => opt.optionNumber === optionNumber);
      if (index === -1) return;

      const option = this.calculatedOptions[index];

      // 매입가 계산
      // 소싱 목표 판매가 상품에 소싱 매입원가가 실려 있으면 그 값이 매입가다 (recalculateOption 과 같은 규칙)
      const sourcingCost = this.selectedProduct && this.selectedProduct.sourcingSellingPrice
        ? Number(this.scaledSourcingPricing(option).purchaseCost) || 0
        : 0;
      const purchasePrice = sourcingCost > 0
        ? sourcingCost
        : pricingPolicy.purchaseCost(option.price * (option.bundleQuantity || 1), this.editPolicy());
      const supplyMargin = option.supplyPrice - purchasePrice;
      const sellingMargin = option.sellingPrice - option.supplyPrice;

      console.log(`🔄 마진 재계산 [${optionNumber}]:`, {
        supplyPrice: option.supplyPrice,
        sellingPrice: option.sellingPrice,
        purchasePrice: purchasePrice,
        supplyMargin: supplyMargin,
        sellingMargin: sellingMargin
      });

      // Vue 반응성 보장을 위해 $set 사용
      this.$set(option, 'purchasePrice', purchasePrice);
      this.$set(option, 'supplyMargin', supplyMargin);
      this.$set(option, 'sellingMargin', sellingMargin);
    },

    // 마진 재계산 (인덱스 기반 - 기존 호환성 유지)
    recalculateMargins(index) {
      const option = this.calculatedOptions[index];
      if (!option) return;
      this.recalculateMarginsByOptionNumber(option.optionNumber);
    },

    // 번들 수량 계산
    calculateBundleQuantity(cnyPrice, exchangeRate, marginRate, coupangMarginRate, bundlingType, minMarginAmount, minCoupangMarginAmount, roundUnit = 100) {
      // 단일 상품으로 계산한 가격
      const purchasePrice = cnyPrice * exchangeRate; // 단일 구매가
      const marginRateDecimal = marginRate / 100;
      const coupangRateDecimal = coupangMarginRate / 100;

      const rawSupplyPrice = purchasePrice / (1 - marginRateDecimal);
      const supplyPrice = Math.round(rawSupplyPrice / roundUnit) * roundUnit;

      const rawSellingPrice = supplyPrice / (1 - coupangRateDecimal);
      const sellingPrice = Math.round(rawSellingPrice / roundUnit) * roundUnit;

      // 마진 계산 (단일 상품 기준)
      const supplyMargin = supplyPrice - purchasePrice;
      const sellingMargin = sellingPrice - supplyPrice;

      // 번들링 기준에 따른 조건 확인
      let bundleQuantity = 1;

      // 여기서는 한 개 상품당 마진에 번들 수량을 곱하여 총 마진을 계산
      if (bundlingType === 'marginAmount') {
        // 공급 마진액 기준
        if (supplyMargin < minMarginAmount) {
          // 2개, 4개, 6개... 단위로 번들링
          while (supplyMargin * bundleQuantity < minMarginAmount && bundleQuantity < 20) {
            bundleQuantity += 2;
          }
        }
      } else { // 'coupangMarginAmount'
        // 쿠팡 마진액 기준
        if (sellingMargin < minCoupangMarginAmount) {
          // 2개, 4개, 6개... 단위로 번들링
          while (sellingMargin * bundleQuantity < minCoupangMarginAmount && bundleQuantity < 20) {
            bundleQuantity += 2;
          }
        }
      }

      console.log(`번들 계산: 단일 구매가=${purchasePrice}, 단일 공급가=${supplyPrice}, 단일 공급마진=${supplyMargin}, 번들수량=${bundleQuantity}`);

      return bundleQuantity;
    },

    // 가격 설정 저장
    async savePricing() {
      if (!this.selectedProduct) {
        this.$bvToast.toast('선택된 상품이 없습니다.', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      try {
        console.log('💾 가격 설정 저장 시작');

        // 화면에서 방금 만든 묶음 옵션(아직 product.options 에 없는 것)들의 옵션번호.
        // 이것들은 원본 옵션을 복사해 만들어져 **한글표시사항이 1개 기준**이다 — 저장하면서 무효화한다.
        // 이미 저장돼 있던 묶음은 여기에 들지 않는다. 그쪽까지 무효화하면 라벨을 다시 만들어도
        // 저장 한 번에 도로 지워져 영구 미완료가 된다.
        const savedOptionNumbers = (this.selectedProduct.options || []).map(o => o.optionNumber);
        const newBundleOptionNumbers = this.calculatedOptions
          .filter(opt => opt.isBundleOption && !savedOptionNumbers.includes(opt.optionNumber))
          .map(opt => opt.optionNumber);

        // ========== 1. product.options 배열 업데이트 ==========
        const updatedOptions = this.calculatedOptions.map(opt => {
          // 원본 옵션 찾기
          // 같은 번호가 있으면 그게 우선. originalOptionNumber 폴백은 화면에서 방금 만들어
          // 아직 product.options 에 없는 묶음 옵션용이다. 한 번의 find 로 or 를 걸면 배열에서
          // 앞에 있는 원본 옵션이 먼저 잡혀, 이미 저장된 묶음 옵션의 내용이 원본으로 덮인다.
          const originalOption = this.selectedProduct.options.find(o => o.optionNumber === opt.optionNumber)
            || this.selectedProduct.options.find(o => o.optionNumber === opt.originalOptionNumber);

          // 원본 옵션이 있으면 복사, 없으면 새로 생성
          const updatedOption = originalOption ? this.deepClone(originalOption) : {};

          // 가격 정보 업데이트
          updatedOption.optionNumber = opt.optionNumber;
          updatedOption.korName = opt.korName;
          updatedOption.price = opt.price;
          updatedOption.bundleQuantity = opt.bundleQuantity;
          updatedOption.supplyPrice = opt.supplyPrice;
          updatedOption.sellingPrice = opt.sellingPrice;
          updatedOption.purchasePrice = opt.purchasePrice;
          updatedOption.supplyMargin = opt.supplyMargin;
          updatedOption.sellingMargin = opt.sellingMargin;
          updatedOption.stock = opt.stock;
          updatedOption.pricingStatus = 'completed';
          updatedOption.isBundleOption = opt.isBundleOption || false;
          updatedOption.originalOptionNumber = opt.originalOptionNumber || null;

          // 방금 만든 묶음 옵션은 원본 라벨을 물고 태어난다. 2개를 보내면서 1개짜리 표시가 붙으니
          // 그대로 쓸 수 없다 — 「다시 만들어야 한다」로 보이게 비운다.
          if (newBundleOptionNumbers.includes(updatedOption.optionNumber)) {
            bundleOptionService.invalidateKoreanLabel(updatedOption);
          }

          return updatedOption;
        });

        console.log(`✅ product.options 업데이트 완료 (${updatedOptions.length}개)`);

        // ========== 2. product.coupangDoc.jsonDocument 배열 업데이트 ==========
        const updatedJsonDocument = this.calculatedOptions.map((opt) => {
          // 원본 jsonDocument 찾기
          let originalDoc = null;

          if (opt.isBundleOption && opt.originalOptionNumber) {
            // 번들 옵션: 이미 저장된 묶음이면 **자기 서류**가 1순위다.
            // 원본 서류로 다시 만들면 묶음 옵션에만 해둔 편집이 조용히 날아간다.
            // 화면에서 방금 만든 묶음은 아직 자기 서류가 없으니 원본 서류에서 복제한다.
            const ownOptionIndex = this.selectedProduct.options.findIndex(
              o => o.optionNumber === opt.optionNumber
            );
            const originalOptionIndex = ownOptionIndex !== -1 ? ownOptionIndex
              : this.selectedProduct.options.findIndex(
                o => o.optionNumber === opt.originalOptionNumber
              );
            if (originalOptionIndex !== -1 &&
                this.selectedProduct.coupangDoc.jsonDocument[originalOptionIndex]) {
              originalDoc = this.selectedProduct.coupangDoc.jsonDocument[originalOptionIndex];
            }
          } else {
            // 일반 옵션인 경우: 해당 인덱스의 jsonDocument 사용
            const originalOptionIndex = this.selectedProduct.options.findIndex(
              o => o.optionNumber === opt.optionNumber
            );
            if (originalOptionIndex !== -1 &&
                this.selectedProduct.coupangDoc.jsonDocument[originalOptionIndex]) {
              originalDoc = this.selectedProduct.coupangDoc.jsonDocument[originalOptionIndex];
            }
          }

          // 원본이 없으면 첫 번째 jsonDocument 사용
          if (!originalDoc && this.selectedProduct.coupangDoc.jsonDocument.length > 0) {
            originalDoc = this.selectedProduct.coupangDoc.jsonDocument[0];
          }

          // jsonDocument 생성
          if (opt.isBundleOption) {
            const bundleDoc = this.createBundleJsonDocument(originalDoc, opt);
            // 방금 만든 묶음이면 승계된 한글표시사항 라벨 이미지도 같이 비운다.
            // 옵션 상태만 비우면 서류에는 1개짜리 라벨이 그대로 남아 전송 때 쿠팡으로 간다.
            if (newBundleOptionNumbers.includes(opt.optionNumber)) {
              bundleOptionService.clearKoreanLabelImageInDoc(bundleDoc);
            }
            return bundleDoc;
          } else {
            // 일반 옵션: 원본 복사 후 가격만 업데이트 (productName은 건드리지 않음)
            const doc = this.deepClone(originalDoc);

            // 가격 정보만 업데이트
            if (doc.productPage && doc.productPage.commonAttributes) {
              doc.productPage.commonAttributes.purchasePrice = opt.supplyPrice;
              doc.productPage.commonAttributes.coupangSalePrice = opt.sellingPrice;

              const msrpValue = this.calculateMsrpValue(opt.sellingPrice);
              const fieldName = this.shouldUseOsrp(doc) ? 'osrp' : 'msrp';
              doc.productPage.commonAttributes[fieldName] = msrpValue;

              console.log(`✅ 일반 옵션 가격 업데이트: ${opt.optionNumber} (공급가: ${opt.supplyPrice}, 판매가: ${opt.sellingPrice})`);
            }

            return doc;
          }
        });

        console.log(`✅ product.coupangDoc.jsonDocument 업데이트 완료 (${updatedJsonDocument.length}개)`);

        // ========== 3. product.seo.options 배열 업데이트 ==========
        const updatedSeoOptions = this.calculatedOptions.map(opt => ({
          id: opt.id,
          optionNumber: opt.optionNumber,
          korName: opt.korName
        }));

        console.log(`✅ product.seo.options 업데이트 완료 (${updatedSeoOptions.length}개)`);

        // ========== 4. product.pricing.options 배열 업데이트 ==========
        const updatedPricingOptions = this.calculatedOptions.map(opt => ({
          optionNumber: opt.optionNumber,
          optionName: opt.optionName || opt.korName,
          korName: opt.korName,  // korName 추가
          price: opt.price,
          stock: opt.stock,
          bundleQuantity: opt.bundleQuantity,
          purchasePrice: opt.purchasePrice,
          supplyPrice: opt.supplyPrice,
          sellingPrice: opt.sellingPrice,
          supplyMargin: opt.supplyMargin,
          sellingMargin: opt.sellingMargin
        }));

        const pricingData = {
          info: this.pricingInfo,
          options: updatedPricingOptions
        };

        console.log(`✅ product.pricing.options 업데이트 완료 (${updatedPricingOptions.length}개)`);

        // ========== 5. product.quotation.options 배열 업데이트 (있는 경우) ==========
        let updatedQuotationOptions = null;
        if (this.selectedProduct.quotation && this.selectedProduct.quotation.options) {
          updatedQuotationOptions = this.calculatedOptions.map(opt => {
            // 원본 quotation 정보 찾기
            const originalQuotation = this.selectedProduct.quotation.options.find(
              q => q.optionNumber === opt.originalOptionNumber || q.optionNumber === opt.optionNumber
            );

            if (originalQuotation) {
              // 원본 복사 후 옵션 정보만 업데이트
              const updatedQuotation = this.deepClone(originalQuotation);
              updatedQuotation.optionNumber = opt.optionNumber;
              updatedQuotation.korName = opt.korName;
              return updatedQuotation;
            } else {
              // 원본이 없으면 기본 구조 생성
              return {
                optionNumber: opt.optionNumber,
                korName: opt.korName
              };
            }
          });

          console.log(`✅ product.quotation.options 업데이트 완료 (${updatedQuotationOptions.length}개)`);
        }

        // ========== 6. 서버에 전체 배열 업데이트 요청 ==========
        console.log('💾 서버 업데이트 시작');

        const updateFields = {
          'options': updatedOptions,
          'coupangDoc.jsonDocument': updatedJsonDocument,
          'seo.options': updatedSeoOptions,
          'pricing': pricingData,
          'pricingStatus': 'completed',
          'updatedAt': Date.now()
        };

        if (updatedQuotationOptions) {
          updateFields['quotation.options'] = updatedQuotationOptions;
        }

        // 묶음 옵션을 새로 만들었으면 상품 레벨 한글표시사항도 내린다.
        // 등록전송의 완료 검사는 **상품 레벨** koreanLabelStatus 만 보기 때문에, 옵션만 비우면
        // 1개짜리 라벨을 단 상품이 그대로 전송 대상에 남는다.
        // koreanLabels·koreanLabelImage 는 그대로 둔다 — 묶음이 아닌 옵션들의 라벨은 멀쩡하다.
        if (newBundleOptionNumbers.length) {
          updateFields['koreanLabelStatus'] = null;
        }

        // 서버에 부분 업데이트 요청
        await this.$store.dispatch('updateProductPartial', {
          productId: this.selectedProduct._id,
          updateFields: updateFields
        });

        console.log('✅ 서버 업데이트 완료');

        // 성공 메시지 표시
        this.$bvToast.toast('가격 설정이 완료되었습니다.', {
          title: '성공',
          variant: 'success',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });

        return true;
      } catch (error) {
        console.error('가격 설정 저장 중 오류:', error);
        this.$bvToast.toast('가격 설정 저장 중 오류가 발생했습니다: ' + error.message, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });

        return false;
      }
    },

    // // 다음 단계로 이동
    // async goToNextStep() {
    //   // 먼저 현재 설정 저장
    //   const saved = await this.savePricing();
    //   if (saved) {
    //     // SEO 설정 또는 다음 작업 단계로 이동
    //     this.$emit('next-step', 'seo');
    //   }
    // },

    // 숫자 포맷팅
    formatNumber(value) {
      return new Intl.NumberFormat().format(value || 0);
    },

    // 마진율 계산
    calculateMarginRate(cost, price) {
      if (!cost || !price || cost >= price) return 0;
      return Math.round(((price - cost) / price) * 100);
    },

    // 옵션명 가져오기 (korName 우선 사용, 번들 표시 제거)
    getOptionName(option) {
      console.log('🔍 getOptionName 호출됨:', {
        option: option,
        hasKorName: !!option?.korName,
        hasOptionNumber: !!option?.optionNumber,
        hasOptionName: !!option?.optionName,
        hasOption1: !!option?.option1,
        hasOption2: !!option?.option2
      });

      if (!option) {
        console.log('⚠️ option이 null/undefined');
        return '기본 옵션';
      }

      let name = '';

      // 1. 우선순위: option.korName (직접 접근)
      if (option.korName) {
        name = option.korName;
        console.log('✅ korName 사용:', name);
      }

      // 2. korName이 없으면 optionName 사용
      if (!name && option.optionName) {
        name = option.optionName;
        console.log('✅ optionName 사용:', name);
      }

      // 3. 여전히 없으면 원본 옵션에서 korName 찾기
      if (!name && option.optionNumber && this.selectedProduct && this.selectedProduct.options) {
        const originalOption = this.selectedProduct.options.find(
          opt => opt.optionNumber === option.optionNumber
        );
        if (originalOption) {
          if (originalOption.korName) {
            name = originalOption.korName;
            console.log('✅ 원본 옵션에서 korName 찾음:', name);
          } else if (originalOption.optionName) {
            name = originalOption.optionName;
            console.log('✅ 원본 옵션에서 optionName 찾음:', name);
          } else {
            console.log('⚠️ 원본 옵션에 korName/optionName 없음, originalOption:', originalOption);
          }
        } else {
          console.log('⚠️ 원본 옵션을 찾지 못함, optionNumber:', option.optionNumber);
        }
      }

      // 4. 여전히 없으면 option1과 option2 조합
      if (!name) {
        if (option.option1) {
          name += option.option1.nameTrans || option.option1.name || '';
        }
        if (option.option2) {
          name += ' / ' + (option.option2.nameTrans || option.option2.name || '');
        }
        name = name || '기본 옵션';
        console.log('✅ option1/option2 조합 사용:', name);
      }

      console.log('📤 최종 반환값:', name);
      return name;
    },

    // MSRP 값 계산 (settings의 msrpMultiplier와 roundUnit 사용)
    calculateMsrpValue(sellingPrice) {
      const msrpMultiplier = this.settings.msrpMultiplier || 1.3;
      const roundUnit = this.settings.roundUnit || this.editRoundUnit || 100;
      return Number(Math.round((sellingPrice * msrpMultiplier) / roundUnit) * roundUnit);
    },

    // 스키마에서 osrp 필드 사용 여부 확인 (docObj 기준)
    shouldUseOsrp(docObj) {
      try {
        // 현재 docObj에 osrp 필드가 있는지 확인
        return Object.prototype.hasOwnProperty.call(docObj.productPage.commonAttributes, 'osrp');
      } catch (error) {
        return false;
      }
    },

    // 인덱스 기준으로 osrp 필드 사용 여부 확인
    shouldUseOsrpForIndex(docIndex) {
      try {
        if (this.selectedProduct &&
            this.selectedProduct.coupangDoc &&
            this.selectedProduct.coupangDoc.jsonDocument &&
            this.selectedProduct.coupangDoc.jsonDocument[docIndex] &&
            this.selectedProduct.coupangDoc.jsonDocument[docIndex].productPage &&
            this.selectedProduct.coupangDoc.jsonDocument[docIndex].productPage.commonAttributes) {

          return Object.prototype.hasOwnProperty.call(
            this.selectedProduct.coupangDoc.jsonDocument[docIndex].productPage.commonAttributes,
            'osrp'
          );
        }
        return false;
      } catch (error) {
        return false;
      }
    },

    // ========== 번들 관련 메서드 ==========

    // 깊은 복사 메서드
    deepClone(obj) {
      if (obj === null || typeof obj !== "object") {
        return obj;
      }

      const result = Array.isArray(obj) ? [] : {};

      for (let key of Object.keys(obj)) {
        result[key] = this.deepClone(obj[key]);
      }

      return result;
    },

    // 선택된 옵션 삭제
    async deleteSelectedOptions() {
      if (this.selectedOptions.length === 0) {
        this.$bvToast.toast('삭제할 옵션을 선택해주세요.', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      const confirmed = await this.$bvModal.msgBoxConfirm(
        `선택한 ${this.selectedOptions.length}개의 옵션을 삭제하시겠습니까?`,
        {
          title: '옵션 삭제 확인',
          okVariant: 'danger',
          okTitle: '삭제',
          cancelTitle: '취소',
          centered: true
        }
      );

      if (!confirmed) return;

      try {
        // 선택된 옵션의 optionNumber 목록
        const selectedOptionNumbers = this.selectedOptions.map(opt => opt.optionNumber);

        // calculatedOptions에서 제거
        this.calculatedOptions = this.calculatedOptions.filter(
          opt => !selectedOptionNumbers.includes(opt.optionNumber)
        );

        // 선택 초기화
        this.selectedOptions = [];

        // 즉시 저장
        const saveSuccess = await this.savePricing();

        if (saveSuccess) {
          // 저장 후 데이터 다시 불러오기
          await this.initializeCalculatedOptions();

          this.$bvToast.toast('선택한 옵션이 삭제되었습니다.', {
            title: '성공',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        }
      } catch (error) {
        console.error('옵션 삭제 중 오류:', error);
        this.$bvToast.toast('옵션 삭제 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 번들 옵션 추가 (원본 유지)
    async addBundleOptions() {
      if (this.selectedOptions.length === 0) {
        this.$bvToast.toast('번들로 추가할 옵션을 선택해주세요.', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      // prompt로 수량 입력받기
      const quantityStr = prompt('번들 수량을 입력하세요:', '2');

      if (quantityStr === null) {
        // 취소한 경우
        return;
      }

      const bundleQuantity = parseInt(quantityStr);

      // 유효성 검사
      if (isNaN(bundleQuantity) || bundleQuantity < 2) {
        this.$bvToast.toast('올바른 번들 수량을 입력해주세요 (2 이상의 정수)', {
          title: '입력 오류',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      try {
        const newBundleOptions = [];

        for (const selectedOption of this.selectedOptions) {
          // 번들 옵션 생성 (입력받은 수량으로)
          const bundleOption = this.createBundleOption(selectedOption, bundleQuantity);
          newBundleOptions.push(bundleOption);
        }

        // calculatedOptions에 번들 옵션 추가
        this.calculatedOptions.push(...newBundleOptions);

        // 선택 초기화
        this.selectedOptions = [];

        // 즉시 저장
        const saveSuccess = await this.savePricing();

        if (saveSuccess) {
          // 저장 후 데이터 다시 불러오기
          await this.initializeCalculatedOptions();

          this.$bvToast.toast(`${newBundleOptions.length}개의 번들 옵션이 추가되었습니다. (${bundleQuantity}개 묶음)`, {
            title: '성공',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        }
      } catch (error) {
        console.error('번들 옵션 추가 중 오류:', error);
        this.$bvToast.toast('번들 옵션 추가 중 오류가 발생했습니다: ' + error.message, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 번들 적용 (원본 대체)
    async applyBundleToOptions() {
      if (this.selectedOptions.length === 0) {
        this.$bvToast.toast('번들을 적용할 옵션을 선택해주세요.', {
          title: '알림',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      // prompt로 수량 입력받기
      const quantityStr = prompt('번들 수량을 입력하세요 (1을 입력하면 번들이 해제됩니다):', '2');

      if (quantityStr === null) {
        // 취소한 경우
        return;
      }

      const bundleQuantity = parseInt(quantityStr);

      // 유효성 검사
      if (isNaN(bundleQuantity) || bundleQuantity < 1) {
        this.$bvToast.toast('올바른 번들 수량을 입력해주세요 (1 이상의 정수)', {
          title: '입력 오류',
          variant: 'warning',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
        return;
      }

      try {
        for (const selectedOption of this.selectedOptions) {
          // 원본 옵션의 인덱스 찾기
          const originalIndex = this.calculatedOptions.findIndex(
            opt => opt.optionNumber === selectedOption.optionNumber
          );

          if (originalIndex === -1) continue;

          // 번들 수량 적용
          this.calculatedOptions[originalIndex].bundleQuantity = bundleQuantity;

          // 옵션명 업데이트 — 기존 "(N개묶음)" 을 떼고 N≥2 면 다시 붙인다(1 이면 뗀 이름) · MCP setBundle 과 같은 함수
          this.calculatedOptions[originalIndex].korName =
            bundleOptionService.bundledOptionName(this.getOptionName(selectedOption), bundleQuantity);

          // 가격 재계산
          this.recalculateOption(originalIndex);
        }

        // 선택 초기화
        this.selectedOptions = [];

        // 즉시 저장
        const saveSuccess = await this.savePricing();

        if (saveSuccess) {
          // 저장 후 데이터 다시 불러오기
          await this.initializeCalculatedOptions();

          const message = bundleQuantity === 1
            ? '선택한 옵션의 번들이 해제되었습니다.'
            : `선택한 옵션에 ${bundleQuantity}개 번들이 적용되었습니다.`;

          this.$bvToast.toast(message, {
            title: '성공',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-bottom-right'
          });
        }
      } catch (error) {
        console.error('번들 적용 중 오류:', error);
        this.$bvToast.toast('번들 적용 중 오류가 발생했습니다: ' + error.message, {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right'
        });
      }
    },

    // 번들 옵션 생성 — 계산은 bundleOptionService 가 한다 (자동 작업 경로와 같은 계산을 쓰기 위해)
    createBundleOption(originalOption, bundleQuantity = null) {
      // 1. 번들 수량 결정 (파라미터로 받거나 자동 계산)
      if (bundleQuantity === null) {
        bundleQuantity = this.calculateBundleQuantity(
          originalOption.price,
          this.editExchangeRate,
          this.editMarginRate,
          this.editCoupangMarginRate,
          this.editBundlingType,
          this.editMinMarginAmount,
          this.editMinCoupangMarginAmount
        );
      }

      // 2. 옵션명은 화면 규칙(getOptionName)으로 구해서 넘긴다
      return bundleOptionService.buildBundleOption(
        originalOption,
        bundleQuantity,
        this.bundleSettings,
        this.getOptionName(originalOption)
      );
    },

    // 번들 옵션의 jsonDocument 생성 — 복제·가격 교체는 bundleOptionService 가 한다
    createBundleJsonDocument(originalDoc, bundleOption) {
      // 노출속성에서 바꿔 끼울 원본 옵션명 (화면의 계산용 옵션 목록에서 찾는다)
      const originalOptionName = this.getOptionName(
        this.calculatedOptions.find(opt => opt.optionNumber === bundleOption.originalOptionNumber)
      );

      return bundleOptionService.buildBundleJsonDocument(
        originalDoc,
        bundleOption,
        originalOptionName,
        this.bundleSettings,
        this.selectedProduct && this.selectedProduct.quotationUserEdits
      );
    }
  }
}
</script>

<style scoped>
.pricing-edit {
  padding: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.section-title {
  margin-bottom: 20px;
  text-align: center;
  color: #333;
}

/* 로딩 컨테이너 */
.loading-container {
  text-align: center;
  padding: 40px 0;
}

/* 가격 정책 패널 */
.pricing-policy-panel {
  max-height: 600px;
  overflow-y: auto;
}

/* 테이블 숫자 정렬 */
.text-right {
  text-align: right !important;
}

/* 옵션 입력 스타일 */
.form-control-sm {
  font-size: 0.875rem;
}

/* 입력 필드 너비 조정 */
input[type="number"] {
  min-width: 80px;
}

/* 마진 강조 스타일 */
.text-success {
  font-weight: 600;
}

.text-danger {
  font-weight: 600;
}

/* 테이블 셀 패딩 조정 */
.table td {
  padding: 0.5rem;
  vertical-align: middle;
}

/* 요약 정보 카드 스타일 */
.card-title {
  font-weight: bold;
  color: #495057;
}

/* 버튼 간격 */
.d-flex.gap-2 > * {
  margin-left: 0.5rem;
}

.d-flex.gap-2 > *:first-child {
  margin-left: 0;
}
</style>